import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listProducts, setProductActive } from '../services/productsService';
import { formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import ConfirmDialog from '../components/ui/ConfirmDialog';

export default function ProductsPage() {
  const { ruc, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const canManage = user?.rol !== 'prueba';

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!ruc) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await listProducts({
          ruc,
          search,
          includeInactive,
          cursor: reset ? null : cursor,
        });
        setItems((prev) => (reset ? result.items : [...prev, ...result.items]));
        setCursor(result.cursor || null);
        setHasMore(!!result.hasMore);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruc, search, includeInactive, cursor]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setItems([]);
      setCursor(null);
      fetchPage({ reset: true });
    }, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [ruc, search, includeInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(
    () => [
      { key: 'id', label: 'Código', render: (r) => r.id || r.uid?.slice(0, 6) },
      { key: 'name', label: 'Producto' },
      { key: 'marca', label: 'Marca', render: (r) => r.marca || '—' },
      { key: 'price', label: 'Precio', render: (r) => formatMoney(r.price, r.currency) },
      { key: 'costo', label: 'Costo', render: (r) => formatMoney(r.costo, r.currency) },
      { key: 'stock', label: 'Stock', render: (r) => (r.controlaStock ? r.stock ?? 0 : '—') },
      { key: 'unidad', label: 'Unidad', render: (r) => r.unidad || '—' },
      {
        key: 'controlaLotes',
        label: 'Lotes',
        render: (r) => (r.controlaLotes ? <Badge tone="info">Sí</Badge> : <span className="muted">No</span>),
      },
      {
        key: 'activo',
        label: 'Activo',
        render: (r) =>
          r.activo === false ? <Badge tone="muted">Inactivo</Badge> : <Badge tone="ok">Activo</Badge>,
      },
      {
        key: 'actions',
        label: 'Acciones',
        render: (r) => (
          <div className="row-actions">
            <button type="button" className="btn btn-mini" onClick={(e) => { e.stopPropagation(); navigate(`/admin/productos/${r.uid}`); }}>
              Ver
            </button>
            {canManage ? (
              <button
                type="button"
                className="btn btn-mini btn-ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirm({ uid: r.uid, activo: r.activo !== false, name: r.name });
                }}
              >
                {r.activo === false ? 'Activar' : 'Desactivar'}
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [canManage, navigate]
  );

  const onToggleActive = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await setProductActive(confirm.uid, !confirm.activo);
      setConfirm(null);
      await fetchPage({ reset: true });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Productos</h1>
          <p className="muted">Catálogo real de la empresa · lazy loading limit 10</p>
        </div>
        {canManage ? (
          <Link className="btn btn-primary" to="/admin/productos/nuevo">Nuevo producto</Link>
        ) : null}
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar producto…" />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Incluir inactivos
        </label>
      </div>

      {loading ? (
        <LoadingState label="Cargando productos…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchPage({ reset: true })} />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            keyField="uid"
            onRowClick={(row) => navigate(`/admin/productos/${row.uid}`)}
            empty={<EmptyState title="No hay productos" description="Agrega el primero o carga el catálogo demo desde la app móvil." />}
          />
          <Pagination
            hasMore={hasMore}
            onMore={() => fetchPage()}
            loading={loadingMore}
            shown={items.length}
          />
        </>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.activo ? 'Desactivar producto' : 'Activar producto'}
        message={
          confirm?.activo
            ? `¿Desactivar "${confirm?.name}"? No se eliminará, solo dejará de estar activo.`
            : `¿Activar "${confirm?.name}"?`
        }
        confirmLabel={confirm?.activo ? 'Desactivar' : 'Activar'}
        onConfirm={onToggleActive}
        onCancel={() => setConfirm(null)}
        loading={busy}
      />
    </div>
  );
}
